// policy-verdict-parser.ts
//
// Tách khỏi policy-adapter.ts (giữ mỗi file ≤200 dòng): file này chỉ lo BIẾN ĐỔI VĂN
// BẢN — đọc verdict từ stdout của facade và chuẩn hoá reason. Không quyết định policy,
// không chạm đĩa, không spawn gì.
//
// Luật §9.3: verdict nằm trong JSON, KHÔNG phải ở exit code. Đo được trong pack hiện
// tại: có hook `deny` mà vẫn exit 0. Tin exit code ⇒ fail-open âm thầm.

export type PolicyDecision = "allow" | "deny" | "requires_authority";

export interface PolicyVerdict {
  decision: PolicyDecision;
  reason: string;
  /** Nguồn quyết định — để evidence phân biệt facade với luật nội bộ. */
  source: "facade" | "review-gate" | "forbidden-scope" | "adapter-error";
}

/**
 * Chuẩn hoá reason: một dòng, không rò path tuyệt đối/bí mật, cắt độ dài. Reason đi vào
 * journal + report nên phải ổn định và an toàn để hiển thị.
 */
export function normalizeReason(raw: string): string {
  const oneLine = raw.replace(/\s+/g, " ").trim();
  const redacted = oneLine
    .replace(/\/Users\/[^\s"']+/g, "<path>")
    .replace(/\/home\/[^\s"']+/g, "<path>")
    .replace(/[A-Za-z0-9_-]{24,}/g, (m) => (/^[0-9a-f]+$/i.test(m) ? m : "<redacted>"));
  return redacted.length > 300 ? `${redacted.slice(0, 297)}...` : redacted;
}

/**
 * Lấy verdict từ stdout. Facade có thể in log lẫn JSON ⇒ quét từ DƯỚI LÊN, nhận object
 * JSON hợp lệ CUỐI CÙNG. Không có object nào hợp lệ ⇒ undefined (caller fail-closed).
 */
export function parseVerdict(stdout: string): PolicyVerdict | undefined {
  const candidates = stdout
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("{") && l.endsWith("}"));
  for (const line of candidates.reverse()) {
    try {
      const parsed = JSON.parse(line) as { decision?: string; reason?: string };
      if (
        parsed.decision === "allow" ||
        parsed.decision === "deny" ||
        parsed.decision === "requires_authority"
      ) {
        return {
          decision: parsed.decision,
          reason: normalizeReason(parsed.reason ?? "(facade không nêu lý do)"),
          source: "facade",
        };
      }
    } catch {
      // dòng này không phải JSON hợp lệ — thử dòng trước đó
    }
  }
  return undefined;
}
